import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ShieldCheck, ShieldOff, ShieldQuestion, Plus, Minus } from "lucide-react";
import type Contact from "@/model/interface/Contact";
import { formatDateTime } from "@/util/date";
import ConsentHistory from "@/components/pages/contacts/ConsentHistory";
import GrantConsentDialog from "@/components/pages/contacts/GrantConsentDialog";
import RevokeConsentDialog from "@/components/pages/contacts/RevokeConsentDialog";

interface ContactConsentSectionProps {
  contact: Contact;
  className?: string;
}

const CONSENT_STATUS_CONFIG = {
  EXPRESS: { label: "Express Consent", icon: ShieldCheck, variant: "default" as const, color: "text-green-600" },
  WRITTEN: { label: "Written Consent", icon: ShieldCheck, variant: "default" as const, color: "text-green-600" },
  NONE: { label: "No Consent", icon: ShieldOff, variant: "destructive" as const, color: "text-destructive" },
  UNKNOWN: { label: "Unknown", icon: ShieldQuestion, variant: "secondary" as const, color: "text-muted-foreground" },
};

const CONSENT_TYPE_LABELS: Record<string, string> = {
  CALL: "Calling",
  SMS: "SMS",
  CALL_AND_SMS: "Calling & SMS",
  NONE: "None",
};

const SOURCE_LABELS: Record<string, string> = {
  WEB_FORM: "Web Form",
  VERBAL: "Verbal",
  IMPORT: "Import",
  MANUAL: "Manual",
  INTEGRATION: "Integration",
};

const ContactConsentSection: React.FC<ContactConsentSectionProps> = ({ contact, className }) => {
  const [showGrantDialog, setShowGrantDialog] = useState(false);
  const [showRevokeDialog, setShowRevokeDialog] = useState(false);

  const status = contact.consentStatus ?? "UNKNOWN";
  const config = CONSENT_STATUS_CONFIG[status];
  const StatusIcon = config.icon;

  return (
    <Card className={className}>
      <CardHeader className="p-4">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base">Consent</CardTitle>
          <div className="flex items-center gap-2">
            <Button type="button" variant="outline" size="sm" onClick={() => setShowGrantDialog(true)}>
              <Plus className="h-3.5 w-3.5 mr-1" />
              Grant
            </Button>
            <Button type="button" variant="outline" size="sm" onClick={() => setShowRevokeDialog(true)}>
              <Minus className="h-3.5 w-3.5 mr-1" />
              Revoke
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3 p-4">
        <div className="flex items-center gap-2">
          <StatusIcon className={`h-4 w-4 ${config.color}`} />
          <Badge variant={config.variant}>{config.label}</Badge>
          {contact.consentSource && (
            <span className="text-xs text-muted-foreground">
              via {SOURCE_LABELS[contact.consentSource] ?? contact.consentSource}
            </span>
          )}
        </div>

        {contact.consentGivenAt && (
          <p className="text-xs text-muted-foreground">Granted: {formatDateTime(contact.consentGivenAt)}</p>
        )}

        {contact.consentNotes && <p className="text-xs text-muted-foreground">{contact.consentNotes}</p>}

        {contact.phones && contact.phones.length > 0 && (
          <div className="pt-2 border-t space-y-1">
            <p className="text-xs font-medium text-muted-foreground">Per-phone consent</p>
            {contact.phones.map((phone, i) => (
              <div key={i} className="flex items-center justify-between text-sm">
                <span className="font-mono text-xs">{phone.phoneNumber || phone.phoneNumberE164}</span>
                <Badge variant="outline" className="text-xs">
                  {CONSENT_TYPE_LABELS[phone.consentType ?? "NONE"]}
                </Badge>
              </div>
            ))}
          </div>
        )}

        <ConsentHistory contactId={contact.id} />
      </CardContent>

      <GrantConsentDialog
        contactId={contact.id}
        phones={contact.phones}
        open={showGrantDialog}
        onOpenChange={setShowGrantDialog}
      />

      <RevokeConsentDialog
        contactId={contact.id}
        phones={contact.phones}
        consentStatus={contact.consentStatus}
        open={showRevokeDialog}
        onOpenChange={setShowRevokeDialog}
      />
    </Card>
  );
};

export default ContactConsentSection;
